/**
 * Vault Key Manager
 *
 * Manages vault encryption keys - storage, retrieval, and session state.
 * Keys are stored server-side (wrapped) and cached locally in IndexedDB.
 *
 * ECDH Hybrid Encryption:
 * - Public key stored in IndexedDB (always available for encryption)
 * - Private key encrypted with PIN (requires unlock for decryption)
 * - Allows "click and save" without PIN prompt
 */

import {
  cryptoService,
  WrappedSeed,
  base64ToUint8Array,
  uint8ArrayToBase64,
  generateECDHKeyPair,
  exportECDHPublicKey,
  importECDHPublicKey,
  wrapECDHPrivateKey,
  unwrapECDHPrivateKey,
  encryptWithECDH,
  decryptWithECDH,
  serializeECDHEncrypted,
  deserializeECDHEncrypted,
} from './cryptoService';
import { api } from '../api/client';

// Session state - keys only live in memory
let currentVaultKey: CryptoKey | null = null; // Legacy symmetric key
let currentECDHPrivateKey: CryptoKey | null = null; // ECDH private key (when unlocked)
let cachedECDHPublicKey: CryptoKey | null = null; // ECDH public key (always available)
let lockTimeout: ReturnType<typeof setTimeout> | null = null;

// Lock after 5 minutes of inactivity
const LOCK_TIMEOUT_MS = 5 * 60 * 1000;

// IndexedDB for local caching
const DB_NAME = 'momento_vault';
const STORE_NAME = 'keys';

interface CachedCredentials {
  salt: string;
  lastAccess: number;
  ecdhPublicKey?: string; // Base64 encoded ECDH public key
  ecdhWrappedPrivateKey?: string; // Base64 encoded wrapped private key
}

interface VaultSetupData {
  pin: string;
  recoveryPhrase: string[];
}

interface VaultStatus {
  isSetup: boolean;
  isUnlocked: boolean;
}

/**
 * Open IndexedDB for local key caching
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    // Timeout to prevent infinite hang on blocked IndexedDB
    const timeout = setTimeout(() => {
      reject(new Error('IndexedDB open timed out'));
    }, 5000);

    const request = indexedDB.open(DB_NAME, 1);

    request.onerror = () => {
      clearTimeout(timeout);
      reject(request.error);
    };

    request.onblocked = () => {
      clearTimeout(timeout);
      console.warn('[VaultKeyManager] IndexedDB blocked by another tab');
      reject(new Error('IndexedDB blocked'));
    };

    request.onsuccess = () => {
      clearTimeout(timeout);
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

/**
 * Get cached credentials from IndexedDB
 * IMPORTANT: Explicitly closes DB connection to prevent Safari hangs
 */
async function getCachedCredentials(): Promise<CachedCredentials | null> {
  let db: IDBDatabase | null = null;
  try {
    db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db!.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get('credentials');

      request.onerror = () => {
        db?.close(); // Close on error
        reject(request.error);
      };
      request.onsuccess = () => {
        db?.close(); // Close on success
        resolve(request.result || null);
      };
    });
  } catch {
    db?.close(); // Close on exception
    return null;
  }
}

/**
 * Save credentials to IndexedDB cache
 * IMPORTANT: Explicitly closes DB connection to prevent Safari hangs
 */
async function saveCachedCredentials(credentials: CachedCredentials): Promise<void> {
  let db: IDBDatabase | null = null;
  try {
    db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.put({ id: 'credentials', ...credentials });

      request.onerror = () => {
        db?.close();
        reject(request.error);
      };
      request.onsuccess = () => {
        db?.close();
        resolve();
      };
    });
  } catch (error) {
    db?.close();
    console.error('Failed to cache credentials:', error);
  }
}

/**
 * Clear cached credentials from IndexedDB
 * IMPORTANT: Explicitly closes DB connection to prevent Safari hangs
 */
async function clearCachedCredentials(): Promise<void> {
  let db: IDBDatabase | null = null;
  try {
    db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db!.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete('credentials');

      request.onerror = () => {
        db?.close();
        reject(request.error);
      };
      request.onsuccess = () => {
        db?.close();
        resolve();
      };
    });
  } catch (error) {
    db?.close();
    console.error('Failed to clear cached credentials:', error);
  }
}

/**
 * Reset the auto-lock timer
 */
function resetLockTimer(): void {
  if (lockTimeout) {
    clearTimeout(lockTimeout);
  }

  if (currentVaultKey) {
    lockTimeout = setTimeout(() => {
      lock();
      console.log('Vault auto-locked due to inactivity');
    }, LOCK_TIMEOUT_MS);
  }
}

/**
 * Setup visibility change listener for auto-lock
 */
function setupAutoLock(): void {
  // Lock when tab is hidden (user switches apps/tabs)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && currentVaultKey) {
      lock();
      console.log('Vault locked - app backgrounded');
    }
  });

  // Reset timer on user activity
  ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event => {
    document.addEventListener(event, resetLockTimer, { passive: true });
  });
}

// Initialize auto-lock on module load
if (typeof document !== 'undefined') {
  setupAutoLock();
}

/**
 * Check if vault encryption is set up for the current user
 */
interface EncryptionStatusResponse {
  is_setup: boolean;
}

interface WrappedSeedResponse {
  pin_wrapped_seed: string;
  recovery_wrapped_seed: string;
  salt: string;
  encryption_version: number;
  // ECDH keys for v2 encryption
  ecdh_public_key?: string;
  ecdh_wrapped_private_key?: string;
}

export async function checkVaultStatus(): Promise<VaultStatus> {
  const isUnlocked = currentVaultKey !== null;

  try {
    const response = await api.get<EncryptionStatusResponse>('/vault/encryption/status');

    return {
      isSetup: response.data?.is_setup ?? false,
      isUnlocked,
    };
  } catch {
    return {
      isSetup: false,
      isUnlocked,
    };
  }
}

/**
 * Set up vault encryption for a new user
 * Returns the recovery phrase that user MUST save
 */
export async function setupVaultEncryption(
  pin: string
): Promise<VaultSetupData> {
  // Validate PIN
  const pinValidation = cryptoService.validatePIN(pin);
  if (!pinValidation.valid) {
    throw new Error(pinValidation.error);
  }

  // Generate master seed and recovery phrase
  const masterSeed = cryptoService.generateMasterSeed();
  const recoveryPhrase = await cryptoService.generateRecoveryPhrase();

  // Generate salt for PIN derivation
  const salt = cryptoService.generateSalt();

  // Derive keys for wrapping
  const pinKey = await cryptoService.deriveKeyFromPIN(pin, salt);
  const recoveryKey = await cryptoService.deriveKeyFromPhrase(recoveryPhrase);

  // Wrap master seed with both keys
  const pinWrapped = await cryptoService.wrapMasterSeed(masterSeed, pinKey);
  const recoveryWrapped = await cryptoService.wrapMasterSeed(masterSeed, recoveryKey);

  // Generate ECDH keypair for hybrid encryption ("click and save" without PIN)
  const ecdhKeyPair = await generateECDHKeyPair();
  const ecdhPublicKeyBase64 = await exportECDHPublicKey(ecdhKeyPair.publicKey);
  const ecdhWrappedPrivateKey = await wrapECDHPrivateKey(ecdhKeyPair.privateKey, pinKey);

  // Prepare request payload
  const payload: Record<string, unknown> = {
    pin_wrapped_seed: pinWrapped.encryptedSeed,
    recovery_wrapped_seed: recoveryWrapped.encryptedSeed,
    salt: uint8ArrayToBase64(salt),
    // ECDH keys for v2 encryption
    ecdh_public_key: ecdhPublicKeyBase64,
    ecdh_wrapped_private_key: ecdhWrappedPrivateKey,
  };

  // Send wrapped seeds to server
  await api.post('/vault/encryption/setup', payload);

  // Also set the My Eyes Only PIN (same PIN for unified system)
  // This allows the same PIN to:
  // 1. Unlock vault encryption (client-side)
  // 2. Access My Eyes Only content (server-side access control)
  try {
    await api.post('/vault/pin', { pin });
  } catch {
    // Non-fatal if My Eyes Only PIN fails - encryption is the priority
    console.warn('Failed to set My Eyes Only PIN during setup');
  }

  // Cache credentials locally (including ECDH public key for "click and save")
  await saveCachedCredentials({
    salt: uint8ArrayToBase64(salt),
    lastAccess: Date.now(),
    ecdhPublicKey: ecdhPublicKeyBase64,
    ecdhWrappedPrivateKey: ecdhWrappedPrivateKey,
  });

  // Store keys in memory
  currentVaultKey = await cryptoService.deriveVaultKey(masterSeed);
  currentECDHPrivateKey = ecdhKeyPair.privateKey;
  cachedECDHPublicKey = ecdhKeyPair.publicKey;
  resetLockTimer();

  return {
    pin,
    recoveryPhrase,
  };
}

/**
 * Unlock vault with PIN
 */
export async function unlockWithPIN(pin: string): Promise<boolean> {
  try {
    // Get wrapped seed from server
    const response = await api.get<WrappedSeedResponse>('/vault/encryption/wrapped-seed');
    const { pin_wrapped_seed, salt: serverSalt, ecdh_public_key, ecdh_wrapped_private_key } = response.data!;

    // Use cached salt or server salt
    const cached = await getCachedCredentials();
    const salt = base64ToUint8Array(cached?.salt || serverSalt);

    // Derive PIN key and unwrap master seed
    const pinKey = await cryptoService.deriveKeyFromPIN(pin, salt);
    const wrapped: WrappedSeed = {
      encryptedSeed: pin_wrapped_seed,
      salt: uint8ArrayToBase64(salt),
      version: 1,
    };

    const masterSeed = await cryptoService.unwrapMasterSeed(wrapped, pinKey);

    // Derive vault key (legacy symmetric)
    currentVaultKey = await cryptoService.deriveVaultKey(masterSeed);

    // Load ECDH keys if available
    let ecdhPubKey = ecdh_public_key || cached?.ecdhPublicKey;
    let ecdhWrappedPrivKey = ecdh_wrapped_private_key || cached?.ecdhWrappedPrivateKey;

    // MIGRATION: If no ECDH keys exist, generate them now
    if (!ecdhPubKey || !ecdhWrappedPrivKey) {
      console.log('Upgrading vault to v2 ECDH encryption...');
      try {
        // Generate new ECDH key pair
        const ecdhKeyPair = await generateECDHKeyPair();

        // Export public key as base64
        const publicKeyRaw = await crypto.subtle.exportKey('raw', ecdhKeyPair.publicKey);
        ecdhPubKey = uint8ArrayToBase64(new Uint8Array(publicKeyRaw));

        // Wrap private key with PIN key
        ecdhWrappedPrivKey = await wrapECDHPrivateKey(ecdhKeyPair.privateKey, pinKey);

        // Save to server
        await api.patch('/vault/encryption/upgrade', {
          ecdh_public_key: ecdhPubKey,
          ecdh_wrapped_private_key: ecdhWrappedPrivKey,
        });

        // Store keys in memory
        cachedECDHPublicKey = ecdhKeyPair.publicKey;
        currentECDHPrivateKey = ecdhKeyPair.privateKey;

        console.log('Vault upgraded to v2 ECDH encryption');
      } catch (upgradeError) {
        console.error('Failed to upgrade vault to ECDH:', upgradeError);
        // Continue without ECDH - v1 will still work
      }
    } else {
      // Load existing ECDH keys
      if (ecdhPubKey) {
        cachedECDHPublicKey = await importECDHPublicKey(ecdhPubKey);
      }

      if (ecdhWrappedPrivKey) {
        try {
          currentECDHPrivateKey = await unwrapECDHPrivateKey(ecdhWrappedPrivKey, pinKey);
        } catch (e) {
          console.warn('Failed to unwrap ECDH private key:', e);
        }
      }
    }

    resetLockTimer();

    // Update cache with all credentials
    await saveCachedCredentials({
      salt: uint8ArrayToBase64(salt),
      lastAccess: Date.now(),
      ecdhPublicKey: ecdhPubKey,
      ecdhWrappedPrivateKey: ecdhWrappedPrivKey,
    });

    return true;
  } catch (error) {
    console.error('Failed to unlock vault:', error);
    return false;
  }
}

/**
 * Unlock vault with recovery phrase
 *
 * Note: Recovery phrase can unlock v1 (symmetric) items but NOT v2 (ECDH) items.
 * The ECDH private key is wrapped with PIN, not recovery phrase.
 * User should change their PIN after recovery to re-wrap ECDH key.
 */
export async function unlockWithRecoveryPhrase(words: string[]): Promise<boolean> {
  try {
    // Validate phrase format
    const validation = await cryptoService.validateRecoveryPhrase(words);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Get wrapped seed from server
    const response = await api.get<WrappedSeedResponse>('/vault/encryption/wrapped-seed');
    const { recovery_wrapped_seed, ecdh_public_key } = response.data!;

    // Derive recovery key and unwrap master seed
    const recoveryKey = await cryptoService.deriveKeyFromPhrase(words);
    const wrapped: WrappedSeed = {
      encryptedSeed: recovery_wrapped_seed,
      salt: '', // Not used for recovery
      version: 1,
    };

    const masterSeed = await cryptoService.unwrapMasterSeed(wrapped, recoveryKey);

    // Derive vault key (for v1/symmetric decryption)
    currentVaultKey = await cryptoService.deriveVaultKey(masterSeed);

    // Load ECDH public key (for encryption - still works without PIN)
    // Note: ECDH private key requires PIN, so v2 items won't be decryptable
    // until user changes PIN (which will then unwrap the ECDH private key)
    const cached = await getCachedCredentials();
    const ecdhPubKey = ecdh_public_key || cached?.ecdhPublicKey;
    if (ecdhPubKey) {
      cachedECDHPublicKey = await importECDHPublicKey(ecdhPubKey);
    }

    resetLockTimer();

    return true;
  } catch (error) {
    console.error('Failed to unlock with recovery phrase:', error);
    return false;
  }
}

/**
 * Change PIN (requires current PIN or recovery phrase)
 */
export async function changePIN(
  currentPinOrRecovery: string | string[],
  newPin: string
): Promise<boolean> {
  try {
    // Validate new PIN
    const pinValidation = cryptoService.validatePIN(newPin);
    if (!pinValidation.valid) {
      throw new Error(pinValidation.error);
    }

    // First unlock to get master seed
    let unlocked: boolean;
    if (Array.isArray(currentPinOrRecovery)) {
      unlocked = await unlockWithRecoveryPhrase(currentPinOrRecovery);
    } else {
      unlocked = await unlockWithPIN(currentPinOrRecovery);
    }

    if (!unlocked || !currentVaultKey) {
      throw new Error('Failed to verify current credentials');
    }

    // Get master seed by exporting current vault key
    // (We need to unwrap again to get the raw seed)
    const response = await api.get<WrappedSeedResponse>('/vault/encryption/wrapped-seed');
    const cached = await getCachedCredentials();
    const salt = base64ToUint8Array(cached!.salt);

    let masterSeed: Uint8Array;
    if (Array.isArray(currentPinOrRecovery)) {
      const recoveryKey = await cryptoService.deriveKeyFromPhrase(currentPinOrRecovery);
      masterSeed = await cryptoService.unwrapMasterSeed({
        encryptedSeed: response.data!.recovery_wrapped_seed,
        salt: '',
        version: 1,
      }, recoveryKey);
    } else {
      const pinKey = await cryptoService.deriveKeyFromPIN(currentPinOrRecovery, salt);
      masterSeed = await cryptoService.unwrapMasterSeed({
        encryptedSeed: response.data!.pin_wrapped_seed,
        salt: uint8ArrayToBase64(salt),
        version: 1,
      }, pinKey);
    }

    // Generate new salt and wrap with new PIN
    const newSalt = cryptoService.generateSalt();
    const newPinKey = await cryptoService.deriveKeyFromPIN(newPin, newSalt);
    const newWrapped = await cryptoService.wrapMasterSeed(masterSeed, newPinKey);

    // Re-wrap ECDH private key with new PIN if we have it
    let newEcdhWrappedPrivateKey: string | undefined;
    if (currentECDHPrivateKey) {
      newEcdhWrappedPrivateKey = await wrapECDHPrivateKey(currentECDHPrivateKey, newPinKey);
    }

    // Update server
    const updatePayload: Record<string, string> = {
      pin_wrapped_seed: newWrapped.encryptedSeed,
      salt: uint8ArrayToBase64(newSalt),
    };
    if (newEcdhWrappedPrivateKey) {
      updatePayload.ecdh_wrapped_private_key = newEcdhWrappedPrivateKey;
    }
    await api.put('/vault/encryption/change-pin', updatePayload);

    // Also update My Eyes Only PIN to stay in sync
    try {
      await api.post('/vault/pin', { pin: newPin });
    } catch {
      // Non-fatal - encryption PIN change is the priority
      console.warn('Failed to update My Eyes Only PIN');
    }

    // Update local cache (including re-wrapped ECDH private key)
    // Reuse the cached credentials from earlier to get the ECDH public key
    await saveCachedCredentials({
      salt: uint8ArrayToBase64(newSalt),
      lastAccess: Date.now(),
      ecdhPublicKey: cached?.ecdhPublicKey,
      ecdhWrappedPrivateKey: newEcdhWrappedPrivateKey,
    });

    return true;
  } catch (error) {
    console.error('Failed to change PIN:', error);
    return false;
  }
}

/**
 * Lock the vault (clear ALL keys from memory)
 * SECURITY: Clears all keys including public key to minimize attack surface.
 * User must unlock again to encrypt new items.
 */
export function lock(): void {
  currentVaultKey = null;
  currentECDHPrivateKey = null;
  cachedECDHPublicKey = null; // SECURITY FIX: Also clear public key on lock
  if (lockTimeout) {
    clearTimeout(lockTimeout);
    lockTimeout = null;
  }
}

/**
 * Check if vault is currently unlocked (has private key for decryption)
 */
export function isUnlocked(): boolean {
  return currentVaultKey !== null;
}

/**
 * Check if ECDH encryption is available (public key is cached)
 * This allows "click and save" without unlocking the vault
 */
export function canEncryptWithoutPIN(): boolean {
  return cachedECDHPublicKey !== null;
}

/**
 * Load ECDH public key from cache (for encryption without PIN)
 */
export async function loadECDHPublicKey(): Promise<boolean> {
  console.log('[loadECDHPublicKey] Called, cachedECDHPublicKey:', !!cachedECDHPublicKey);
  if (cachedECDHPublicKey) return true;

  try {
    console.log('[loadECDHPublicKey] Getting cached credentials from IndexedDB...');
    const cached = await getCachedCredentials();
    console.log('[loadECDHPublicKey] Got cached:', !!cached, 'hasEcdhPublicKey:', !!cached?.ecdhPublicKey);

    if (cached?.ecdhPublicKey) {
      console.log('[loadECDHPublicKey] Importing ECDH public key...');
      cachedECDHPublicKey = await importECDHPublicKey(cached.ecdhPublicKey);
      console.log('[loadECDHPublicKey] Successfully imported key');
      return true;
    }
  } catch (e) {
    console.warn('[loadECDHPublicKey] Failed to load ECDH public key from cache:', e);
  }

  console.log('[loadECDHPublicKey] No key found, returning false');
  return false;
}

/**
 * Get the current vault key (for encryption/decryption operations)
 * Throws if vault is locked
 */
export function getVaultKey(): CryptoKey {
  if (!currentVaultKey) {
    throw new Error('Vault is locked - unlock first');
  }
  resetLockTimer();
  return currentVaultKey;
}

/**
 * Encrypt data using the current vault key (legacy v1 - requires unlock)
 */
export async function encryptData(data: ArrayBuffer): Promise<Uint8Array> {
  const key = getVaultKey();
  return cryptoService.encryptFile(data, key);
}

/**
 * Decrypt data using the current vault key (legacy v1 - requires unlock)
 */
export async function decryptData(encrypted: Uint8Array): Promise<ArrayBuffer> {
  const key = getVaultKey();
  return cryptoService.decryptFile(encrypted, key);
}

/**
 * Encrypt data using ECDH (v2 - NO PIN required!)
 * Uses cached public key to encrypt. Can be called even when vault is locked.
 * Returns serialized encrypted data that includes the ephemeral public key.
 */
export async function encryptDataWithECDH(data: ArrayBuffer): Promise<Uint8Array> {
  // Try to load public key if not already cached
  if (!cachedECDHPublicKey) {
    const loaded = await loadECDHPublicKey();
    if (!loaded) {
      throw new Error('ECDH public key not available - encryption setup required');
    }
  }

  const ecdhEncrypted = await encryptWithECDH(new Uint8Array(data), cachedECDHPublicKey!);
  return serializeECDHEncrypted(ecdhEncrypted);
}

/**
 * Decrypt data encrypted with ECDH (v2 - requires unlock)
 * Uses the private key which is only available after unlock.
 */
export async function decryptDataWithECDH(encrypted: Uint8Array): Promise<ArrayBuffer> {
  if (!currentECDHPrivateKey) {
    throw new Error('Vault is locked - unlock required for decryption');
  }

  const ecdhData = deserializeECDHEncrypted(encrypted);
  const decrypted = await decryptWithECDH(ecdhData, currentECDHPrivateKey);
  return decrypted.buffer as ArrayBuffer;
}

/**
 * Smart encrypt: Uses ECDH if available (no PIN needed), falls back to symmetric
 * This is the main function for "click and save" functionality
 */
export async function smartEncrypt(data: ArrayBuffer): Promise<{ data: Uint8Array; version: number }> {
  console.log('[smartEncrypt] Starting, cachedECDHPublicKey:', !!cachedECDHPublicKey);

  // Try ECDH first (no PIN required)
  if (cachedECDHPublicKey) {
    console.log('[smartEncrypt] Using cached ECDH key');
    return {
      data: await encryptDataWithECDH(data),
      version: 2, // ECDH
    };
  }

  console.log('[smartEncrypt] No cached key, trying to load from IndexedDB...');
  const loaded = await loadECDHPublicKey();
  console.log('[smartEncrypt] loadECDHPublicKey result:', loaded);

  if (loaded) {
    console.log('[smartEncrypt] Using loaded ECDH key');
    return {
      data: await encryptDataWithECDH(data),
      version: 2, // ECDH
    };
  }

  // Fall back to symmetric (requires vault to be unlocked)
  console.log('[smartEncrypt] No ECDH key, checking symmetric key:', !!currentVaultKey);
  if (currentVaultKey) {
    return {
      data: await encryptData(data),
      version: 1, // Symmetric
    };
  }

  throw new Error('No encryption keys available - set up vault encryption first');
}

/**
 * Detect if encrypted data is ECDH format (v2)
 * ECDH format: version(1) + keyLen(2) + ephemeralKey(65-97) + iv(12) + ciphertext
 * P-256 public keys are 65 bytes uncompressed or 33 bytes compressed
 */
function detectECDHFormat(data: Uint8Array): boolean {
  if (data.length < 80) return false; // Too small for ECDH

  const version = data[0];
  if (version !== 2) return false;

  // Check key length is reasonable for P-256 (33-97 bytes)
  const keyLength = (data[1] << 8) | data[2];
  if (keyLength < 33 || keyLength > 97) return false;

  // Data should be long enough: 1 + 2 + keyLen + 12 (IV) + at least some ciphertext
  if (data.length < 1 + 2 + keyLength + 12 + 16) return false;

  return true;
}

/**
 * Smart decrypt: Detects version and uses appropriate method
 * If version is not provided, attempts to auto-detect from data format
 */
export async function smartDecrypt(encrypted: Uint8Array, version?: number): Promise<ArrayBuffer> {
  // Auto-detect if version not provided
  const detectedVersion = version ?? (detectECDHFormat(encrypted) ? 2 : 1);

  if (detectedVersion >= 2) {
    return decryptDataWithECDH(encrypted);
  }
  return decryptData(encrypted);
}

/**
 * Reset vault encryption (dangerous - loses all encrypted data!)
 */
export async function resetVaultEncryption(): Promise<void> {
  lock();
  await clearCachedCredentials();
  await api.delete('/vault/encryption');
}

// Export as service object
export const vaultKeyManager = {
  checkVaultStatus,
  setupVaultEncryption,
  unlockWithPIN,
  unlockWithRecoveryPhrase,
  changePIN,
  lock,
  isUnlocked,
  getVaultKey,
  // Legacy symmetric encryption (requires unlock)
  encryptData,
  decryptData,
  // ECDH encryption (v2 - "click and save" without PIN)
  canEncryptWithoutPIN,
  loadECDHPublicKey,
  encryptDataWithECDH,
  decryptDataWithECDH,
  // Smart encryption (auto-selects best method)
  smartEncrypt,
  smartDecrypt,
  resetVaultEncryption,
};

export default vaultKeyManager;
